import { type FC, type ReactNode } from "react";

import { Group, Table, Text } from "@mantine/core";
import { clsx } from "clsx";

import { formatMilliUsd } from "../../../../lens-creator-payout/utils";
import { formatWithUSFormat } from "../../../../lens-stats/formatters/formatNumbers";
import { type LensPlusPayoutRow } from "../../../models";
import { formatMonthYear, isAwaitingPayout, toDisplayedSubscriptions } from "../../../utils";

import styles from "./EnrolledProgram.module.css";
import { PayoutStatusChip } from "./PayoutStatusChip";
import {
    EmptyValue,
    LensPlusScoreColumn,
    LensPlusUsersColumn,
    MissingPayoutBreakdown,
    MonthColumn,
    NoPayoutsYet,
    PayoutColumn,
    PayoutHistoryHeading,
    StatusPending,
    SubscriptionsColumn,
} from "./messages.generated";

// Below this the five columns stop fitting the lens-details column and the table scrolls.
const TABLE_MIN_WIDTH = 520;

const NUMERIC_COLUMNS = [LensPlusUsersColumn, SubscriptionsColumn, LensPlusScoreColumn, PayoutColumn];

interface LensPlusPayoutHistoryTableProps {
    rows: LensPlusPayoutRow[];
}

const metric = (value: number | null | undefined): ReactNode =>
    typeof value === "number" ? formatWithUSFormat(value) : <EmptyValue />;

const PayoutCell: FC<{ row: LensPlusPayoutRow }> = ({ row }) => {
    if (row.amountMilliUsd !== null) {
        return <>{formatMilliUsd(row.amountMilliUsd)}</>;
    }
    return isAwaitingPayout(row.status) ? (
        <Text span inherit className={styles.pendingPayout}>
            <StatusPending />
        </Text>
    ) : (
        <EmptyValue />
    );
};

export const LensPlusPayoutHistoryTable: FC<LensPlusPayoutHistoryTableProps> = ({ rows }) => (
    <div>
        <Text component="h5" className={styles.sectionHeading}>
            <PayoutHistoryHeading />
        </Text>
        {rows.some((row) => row.amountMilliUsd !== null && !row.totals) && (
            <Text className={styles.caption} mb="xs">
                <MissingPayoutBreakdown />
            </Text>
        )}
        {rows.length === 0 ? (
            <Text className={styles.caption}>
                <NoPayoutsYet />
            </Text>
        ) : (
            <Table.ScrollContainer minWidth={TABLE_MIN_WIDTH}>
                <Table className={styles.payoutTable}>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>
                                <MonthColumn />
                            </Table.Th>
                            {NUMERIC_COLUMNS.map((Column) => (
                                <Table.Th key={Column.displayName} className={styles.numericCell}>
                                    <Column />
                                </Table.Th>
                            ))}
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {rows.map((row) => (
                            <Table.Tr key={row.month}>
                                <Table.Td>
                                    <Group className={styles.monthGroup}>
                                        <Text span inherit className={styles.emphasized}>
                                            {formatMonthYear(row.month)}
                                        </Text>
                                        <PayoutStatusChip status={row.status} paidOn={row.payoutDate} />
                                    </Group>
                                </Table.Td>
                                <Table.Td className={styles.numericCell}>
                                    {metric(row.totals?.subscriberActionsUu)}
                                </Table.Td>
                                <Table.Td className={styles.numericCell}>
                                    {metric(row.totals && toDisplayedSubscriptions(row.totals.subscribeCount))}
                                </Table.Td>
                                <Table.Td className={clsx(styles.numericCell, styles.emphasized)}>
                                    {metric(row.totals?.lensScore)}
                                </Table.Td>
                                <Table.Td className={clsx(styles.numericCell, styles.emphasized)}>
                                    <PayoutCell row={row} />
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Table.ScrollContainer>
        )}
    </div>
);
